/**
 * Scenario Manager Component
 * Save, load, and manage named scenarios
 */

import React, { useState } from 'react';
import { 
  X, 
  FolderOpen, 
  Trash2, 
  Copy, 
  Download,
  Plus,
  Clock,
} from 'lucide-react';

export default function ScenarioManager({
  scenarios,
  currentScenarioId,
  onLoad,
  onDelete,
  onDuplicate,
  onClose,
  onSaveNew,
}) {
  const [newName, setNewName] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  
  const handleSaveNew = () => {
    if (newName.trim()) {
      onSaveNew(newName.trim());
      setNewName('');
      setShowNewForm(false);
    }
  };
  
  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-5 h-5 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-900">Saved Scenarios</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Save New */}
          {showNewForm ? (
            <div className="mb-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
              <label className="block text-sm font-medium text-primary-900 mb-2">
                Scenario Name
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveNew()}
                  placeholder="e.g., Q1 2026 Projection"
                  className="flex-1 input-field"
                  autoFocus
                />
                <button onClick={handleSaveNew} className="btn-primary btn-sm">
                  Save
                </button>
                <button 
                  onClick={() => setShowNewForm(false)} 
                  className="btn-ghost btn-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowNewForm(true)}
              className="w-full mb-4 p-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Save Current as New Scenario
            </button>
          )}
          
          {/* Scenario List */}
          {scenarios.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No saved scenarios yet</p>
              <p className="text-sm mt-1">Save your current configuration to access it later</p>
            </div>
          ) : (
            <div className="space-y-2">
              {scenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className={`p-4 rounded-lg border transition-all ${
                    scenario.id === currentScenarioId
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-900">{scenario.name}</h3>
                      <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(scenario.updatedAt)}</span>
                      </div>
                      {scenario.data?.cohorts && (
                        <p className="text-xs text-slate-500 mt-1">
                          {scenario.data.cohorts.length} cohorts, 
                          {' '}{scenario.data.cohorts.reduce((sum, c) => sum + c.count, 0)} employees
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onLoad(scenario.id)}
                        className="btn-primary btn-xs"
                      >
                        Load
                      </button>
                      <button
                        onClick={() => onDuplicate(scenario.id)}
                        className="btn-ghost btn-xs"
                        title="Duplicate"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this scenario?')) {
                            onDelete(scenario.id);
                          }
                        }}
                        className="btn-ghost btn-xs text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <p className="text-xs text-slate-500">
            Scenarios are saved locally in your browser. They won't be shared or synced across devices.
          </p>
        </div>
      </div>
    </div>
  );
}
